/**
 * ContactListItem — uma linha da lista de contatos da sidebar.
 * Avatar (com logo do canal) + nome + badges + última mensagem +
 * status do funil + tags + botão arquivar (hover).
 */

import React from 'react';
import {
  Check,
  CheckCircle2,
  CheckCheck,
  Clock,
  Mic,
  Video as VideoIcon,
  ImageIcon,
  FileText,
  Paperclip,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import StatusTag from './StatusTag.tsx';
import SidebarTags from './SidebarTags.tsx';
import { formatLastMessage } from './utils.ts';
import ContactAvatar from '../ContactAvatar.tsx';
import ChannelBadge from './ChannelBadge.tsx';
import AttendanceBadge from './AttendanceBadge.tsx';
import DeliveryFailureHint from './DeliveryFailureHint.tsx';
import { resolveContactDisplayChannel } from '../../utils/contactDisplayChannel.ts';
import { useContactAttendanceSummary } from './useContactAttendanceSummary.ts';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { formatBrazilianPhone } from '../../services/api.ts';
import type { Contact } from './types.ts';

interface ContactListItemProps {
  contact: Contact;
  attendanceEnabled?: boolean;
  isSelected: boolean;
  isArchivedView: boolean;
  isArchiving: boolean;
  isTyping?: boolean;
  showPhone: boolean;
  displayedTimestamp: string;
  onSelect: () => void;
  onArchiveToggle: () => void;
  onTaskClick: () => void;
}

const LastMessageStatusIcon: React.FC<{ status?: Contact['lastMessageStatus'] }> = ({ status }) => {
  const className = status === 'read' ? 'text-sminy-primary' : 'text-gray-400';

  switch (status) {
    case 'sending':
      return <Clock className="h-3 w-3 text-gray-400" />;
    case 'sent':
      return <Check className="h-3 w-3 text-gray-400" />;
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-gray-400" />;
    case 'read':
      return <CheckCheck className={`h-3 w-3 ${className}`} />;
    default:
      return null;
  }
};

const ContactListItem: React.FC<ContactListItemProps> = ({
  contact,
  attendanceEnabled = false,
  isSelected,
  isArchivedView,
  isArchiving,
  isTyping = false,
  showPhone,
  displayedTimestamp,
  onSelect,
  onArchiveToggle,
  onTaskClick,
}) => {
  const { isDark } = useTheme();
  const { ref, summary } = useContactAttendanceSummary(contact.id, attendanceEnabled);
  const preview = formatLastMessage(contact.lastMessage, contact.lastMessageType);
  const PreviewIcon =
    preview.type === 'audio' ? Mic :
    preview.type === 'video' ? VideoIcon :
    preview.type === 'image' ? ImageIcon :
    preview.type === 'document' ? FileText :
    preview.type === 'media' ? Paperclip : null;

  const hasUnread = contact.unreadCount > 0;
  const hasTasks = !!(contact.pending_tasks_count && contact.pending_tasks_count > 0);
  const shouldShowLastMessageStatus = contact.lastMessageFromMe === true && Boolean(contact.lastMessageStatus);
  const shouldShowFailure = shouldShowLastMessageStatus && contact.lastMessageStatus === 'failed';
  const shouldShowTyping = isSelected && isTyping;

  const containerClass = isSelected ? 'border-l-sminy-primary' : 'border-l-transparent';

  const taskBgClass =
    contact.next_task?.priority === 'urgent' ? 'bg-rose-500' :
    contact.next_task?.priority === 'high' ? 'bg-orange-500' :
    contact.next_task?.priority === 'medium' ? 'bg-yellow-500' :
    'bg-sminy-primary';

  return (
    <div
      ref={ref}
      className={`relative group w-full p-3 flex items-start gap-3 overflow-hidden border-l-4 border-b last:border-b-0 ${isDark ? 'border-b-gray-700' : 'border-b-sminy-border'} ${containerClass}`}
    >
      <span
        aria-atomic="true"
        aria-live="polite"
        className="sr-only"
        data-testid="contact-list-typing-announcement"
      >
        {shouldShowTyping ? `${contact.name || 'Contato'} está digitando` : ''}
      </span>

      {/* Botão arquivar (hover) */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onArchiveToggle();
          }}
          className="p-1.5 bg-white/95 backdrop-blur-sm shadow-md border border-gray-200 rounded-full hover:border-sminy-primary/40 hover:text-sminy-primary transition-colors"
          title={isArchivedView ? 'Desarquivar' : 'Arquivar'}
          disabled={isArchiving}
        >
          {isArchivedView ? (
            <ArchiveRestore className="h-4 w-4 text-sminy-primary" />
          ) : (
            <Archive className="h-4 w-4 text-gray-500" />
          )}
        </button>
      </div>

      <div
        onClick={onSelect}
        role="presentation"
        className="relative w-full flex items-start gap-3 text-left"
      >
        <button
          type="button"
          aria-label={`Selecionar conversa com ${contact.name || contact.phone}`}
          aria-pressed={isSelected}
          onClick={event => { event.stopPropagation(); onSelect(); }}
          className="absolute inset-0 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary/40"
        />
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <ContactAvatar photo={contact.photo} name={contact.name || contact.phone} className="w-10 h-10 lg:w-12 lg:h-12 rounded-full" iconClassName="w-5 h-5 lg:w-6 lg:h-6" />
          <ChannelBadge channel={resolveContactDisplayChannel(contact.active_channel, contact.lastMessageSourceChannel)} />
        </div>

        {/* Conteúdo (nome, timestamp, mensagem, status, tags) */}
        <div className="relative flex-1 min-w-0 flex flex-col text-left">
          {/* Linha 1: nome + indicators + timestamp */}
          <div className="flex items-center justify-between min-w-0">
            <p
              className={`text-xs lg:text-sm flex-1 min-w-0 mr-1 flex items-center gap-1.5 ${
                hasUnread ? (isDark ? 'font-bold text-gray-100' : 'font-bold text-gray-900') : (isDark ? 'font-medium text-gray-200' : 'font-medium text-gray-700')
              }`}
            >
              <span className="truncate">{contact.name}</span>

            </p>
            <div className="flex items-center gap-1 flex-shrink-0">
              {hasUnread && (
                <div className="flex-shrink-0 w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] lg:text-xs font-bold">
                  {contact.unreadCount > 99 ? '99+' : contact.unreadCount}
                </div>
              )}
              {hasTasks && (
                <button
                  type="button"
                  aria-label={`${contact.pending_tasks_count} tarefa(s) pendente(s)`}
                  className={`flex-shrink-0 w-4 h-4 lg:w-5 lg:h-5 rounded-full text-white flex items-center justify-center text-[10px] lg:text-xs font-bold cursor-pointer ${taskBgClass}`}
                  title={`${contact.pending_tasks_count} tarefa(s) pendente(s)${contact.next_task ? ` - Próxima: ${contact.next_task.title}` : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick();
                  }}
                >
                  <CheckCircle2 className="w-2 h-2 lg:w-3 lg:h-3" />
                </button>
              )}
              {contact.timestampNumber !== 0 && (
                <span
                  className={`text-xs flex-shrink-0 hidden md:inline ${
                    hasUnread ? (isDark ? 'text-emerald-300 font-medium' : 'text-emerald-700 font-medium') : (isDark ? 'text-gray-400' : 'text-gray-500')
                  }`}
                >
                  {displayedTimestamp}
                </span>
              )}
            </div>
          </div>

          {showPhone && (
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
              <span className={`truncate text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatBrazilianPhone(contact.phone)}
              </span>
              {contact.possible_duplicate && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    isDark
                      ? 'bg-amber-900/50 text-amber-200'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  Possível duplicidade
                </span>
              )}
            </div>
          )}

          {/* Linha 2: última mensagem */}
          {(preview.text || shouldShowTyping || shouldShowFailure) && (
            <div
              className={`text-xs mt-1 flex min-w-0 items-center gap-1 ${
                hasUnread ? (isDark ? 'text-gray-200 font-medium' : 'text-gray-700 font-medium') : (isDark ? 'text-gray-400' : 'text-gray-500')
              }`}
            >
              {shouldShowTyping ? (
                <span
                  aria-hidden="true"
                  className="flex h-4 min-w-0 flex-1 items-center truncate"
                  data-testid="contact-list-typing-indicator"
                >
                  digitando...
                </span>
              ) : (
                <div className="flex min-w-0 flex-1 items-center">
                  {shouldShowLastMessageStatus && (
                    contact.lastMessageStatus === 'failed'
                      ? <DeliveryFailureHint compact error={contact.lastMessageDeliveryError} />
                      : <span className="mr-1 grid h-3.5 w-3.5 flex-shrink-0 place-items-center" title="Status da última mensagem">
                          <LastMessageStatusIcon status={contact.lastMessageStatus} />
                        </span>
                  )}
                  {PreviewIcon && <PreviewIcon className="w-3 h-3 mr-1 flex-shrink-0" />}
                  <span className="truncate">{preview.text}</span>
                </div>
              )}
            </div>
          )}

          {/* Linha 3: funnel + atendimento + tags */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 min-w-0">
            <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-full">
              {contact.funnel_stage && (
                <StatusTag status={contact.funnel_stage} className="flex-shrink-0" />
              )}
              <AttendanceBadge humanMode={contact.human_mode === true} summary={summary} />
              <SidebarTags tags={contact.tags} maxTags={2} className="flex-shrink-0" />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactListItem;
